import { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { message } from 'antd';

import { usePatchBeraJobSummary } from '@/hooks';
import { useBeraReportsByJobSummary } from '@/components/views/BeraJob/hooks/useBeraReportV2';
import { buildPersistedAssignments, findValidationMessage, listAssignedFileIds } from './utils';
import type { BeraReportV2, BeraSegmentAssignment } from '@/types/Bera';

const REPORTS_LIMIT = 100;
const FIRST_PAGE_OFFSET = 1;
const NO_SUMMARY = '';
const NONE = 0;

interface UsePartialRevisionParams {
	isEnabled: boolean;
	organizationId: string;
	companyId: string;
	beraJobSummaryId: string;
}

export interface SaveAssignmentsParams {
	fileId: string;
	segmentIds: string[];
}

export interface UsePartialRevisionResult {
	reports: BeraReportV2[];
	isLoadingReports: boolean;
	isPatching: boolean;
	isPatched: boolean;
	selectedFileIds: string[];
	assignments: BeraSegmentAssignment[];
	handleSelectFiles: (fileIds: string[]) => void;
	handleSaveAssignments: (params: SaveAssignmentsParams) => void;
	handleRemoveAssignment: (segmentId: string) => void;
	submitPartialRevision: () => Promise<string | null>;
}

export function usePartialRevision({
	isEnabled,
	organizationId,
	companyId,
	beraJobSummaryId
}: UsePartialRevisionParams): UsePartialRevisionResult {
	const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
	const [assignments, setAssignments] = useState<BeraSegmentAssignment[]>([]);

	const summaryId = isEnabled ? beraJobSummaryId : NO_SUMMARY;

	const { data: reportList, isFetching: isLoadingReports } = useBeraReportsByJobSummary({
		organization_id: organizationId,
		company_id: companyId,
		bera_job_summary_id: summaryId,
		limit: REPORTS_LIMIT,
		offset: FIRST_PAGE_OFFSET,
		only_not_evaluated: false
	});

	const { mutateAsync: patchJobSummary, isLoading: isPatching } = usePatchBeraJobSummary();

	const reports = reportList?.rows ?? [];
	const persistedAssignments = buildPersistedAssignments(reports);
	const isPatched = persistedAssignments.length > NONE;
	const persistedFileIds = listAssignedFileIds(persistedAssignments);

	function handleSelectFiles(fileIds: string[]): void {
		const kept = assignments.filter((assignment) => fileIds.includes(assignment.file_id));

		setSelectedFileIds(fileIds);
		setAssignments(kept);
	}

	function handleSaveAssignments({ fileId, segmentIds }: SaveAssignmentsParams): void {
		const untouched = assignments.filter(
			(assignment) => assignment.file_id !== fileId && !segmentIds.includes(assignment.segment_id)
		);
		const saved = segmentIds.map((segmentId) => ({ segment_id: segmentId, file_id: fileId }));

		setAssignments([...untouched, ...saved]);

		if (segmentIds.length > 0 && !selectedFileIds.includes(fileId)) {
			setSelectedFileIds([...selectedFileIds, fileId]);
		}
	}

	function handleRemoveAssignment(segmentId: string): void {
		const kept = assignments.filter((assignment) => assignment.segment_id !== segmentId);

		setAssignments(kept);
	}

	async function submitPartialRevision(): Promise<string | null> {
		const validationMessage = findValidationMessage({ reports, assignments, selectedFileIds });

		if (validationMessage) {
			message.error(I18n.get(validationMessage));
			return null;
		}

		const params = {
			id: beraJobSummaryId,
			organization_id: organizationId,
			company_id: companyId,
			assignments
		};

		const patched = await patchJobSummary(params);

		return patched.file.id;
	}

	const currentFileIds = isPatched ? persistedFileIds : selectedFileIds;
	const currentAssignments = isPatched ? persistedAssignments : assignments;

	return {
		reports,
		isLoadingReports,
		isPatching,
		isPatched,
		selectedFileIds: currentFileIds,
		assignments: currentAssignments,
		handleSelectFiles,
		handleSaveAssignments,
		handleRemoveAssignment,
		submitPartialRevision
	};
}
